import { Injectable, OnDestroy } from '@angular/core';
import { DependencyModel } from '@data-layer/models/dependencies';
import { CBP_MESSAGE, ENTITY_IDENTIFIER_NOT_ON_FILE } from '@constants/cbp-messages';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { ManagementEntityEventModel } from '@data-layer/models/management';
import { DependenciesRepository, EntrySummaryMessagesRepository, IsfMessagesRepository } from '@data-layer/repositories';
import { EntrySummaryValidationService } from '@services/entry-summary-validation.service';
import { GenericService } from '@services/generic.service';
import { DependencyHelper } from '@helpers/dependency.helper';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { enumIsfState } from '@data-layer/enums/isf';
import { IsfModel } from '@data-layer/models/isf';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { DependenciesService } from '@services/dependencies.service';
import { IsfHelper } from '@helpers/isf.helper';
import { IsfValidationService } from '@services/isf-validation.service';
import { EntryErrorModel } from '@data-layer/models/entry-summaries';
import { enumDependencyModelType } from '@data-layer/enums/dependencies';
import { UtilsHelper } from '@helpers/utils-helper';

@Injectable({
  providedIn: 'root',
})
export class IsfCBPMessagesService implements OnDestroy {
  public isf: IsfModel = null;
  public cbpMessages: any[];
  public errorMessages: any[];
  public importerDep: DependencyModel;
  public consigneeDep: DependencyModel;

  public isShowLoader$: ReplaySubject<boolean> = new ReplaySubject();
  public isShowCbpLogLoader$: ReplaySubject<boolean> = new ReplaySubject();
  public cbpLogMessages$: ReplaySubject<ManagementEntityEventModel[]> = new ReplaySubject();
  public messages$: ReplaySubject<string[]> = new ReplaySubject();
  public messagesLoaded$: ReplaySubject<void> = new ReplaySubject();

  private readonly cbpMessageStates: enumIsfState[] = [
    enumIsfState.REJECTED_CBP,
    enumIsfState.NO_BILL_ON_FILE,
    enumIsfState.BILL_TYPE_NOT_MATCHED,
    enumIsfState.APPROVED,
  ];
  private readonly errorMessagesStates: enumIsfState[] = [enumIsfState.NOT_VALID_AT_PROVIDER, enumIsfState.UNRECOVERABLE];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private isfMessagesRepository: IsfMessagesRepository,
    private dependenciesRepository: DependenciesRepository,
    private entrySummaryMessagesRepository: EntrySummaryMessagesRepository,
    private entrySummaryValidationService: EntrySummaryValidationService,
    private genericService: GenericService,
    private translate: TranslateService,
    private isfValidationService: IsfValidationService,
  ) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public initData(isf: IsfModel): void {
    this.isf = isf;

    this.cbpMessages = [];
    this.errorMessages = [];
    this.importerDep = null;
    this.consigneeDep = null;
    this.isShowLoader$.next(false);
    this.isShowCbpLogLoader$.next(false);
    this.cbpLogMessages$.next([]);
    this.messages$.next([]);

    if (!!this.isf) {
      this.getCbpLogMessages(isf.id);
      this.fetchCbpMessages(isf.id);
      this.fetchErrorMessages(isf.id);
    }
  }

  public showTranslatedKey(message: any): boolean {
    if (!['CBP_MESSAGES.ENTITY_IDENTIFIER_NOT_ON_FILE'].includes(message.key)) {
      return true;
    }
    const code: enumEntityModelCode = this.getEntityCodeFromCbp(message);
    const dep: DependencyModel = this.getDepByKey(message.key, code);
    return !dep;
  }

  public registerMessage(message: any): string {
    const code: enumEntityModelCode = this.getEntityCodeFromCbp(message);

    return DependencyHelper.registerMessage(this.getDepByKey(message.key, code));
  }

  public getEntityTypeText(message: any): string {
    const code: enumDependencyModelType = DependencyHelper.getEntityTypeByCode(this.getEntityCodeFromCbp(message));
    return DependencyHelper.getEntityTypeText(code);
  }

  public getCbpLogMessages(id: number): void {
    this.isShowCbpLogLoader$.next(true);
    this.isfMessagesRepository
      .getCbpRawMessages(id, false, this.genericService.isPrinting$.getValue())
      .pipe(
        finalize(() => {
          this.isShowCbpLogLoader$.next(false);
        }),
        take(1),
      )
      .subscribe((messages: ManagementEntityEventModel[]) => {
        this.cbpLogMessages$.next(messages);
      });
  }

  public fetchCbpMessages(id: number): void {
    const isState: boolean = this.cbpMessageStates.includes(this.isf.state);

    if (isState) {
      this.isShowLoader$.next(true);
      this.isfMessagesRepository
        .getCBPMessages(id, this.genericService.isPrinting$.getValue())
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: Array<{ key: string; params: string[] }>) => {
          this.cbpMessages = data
            .map(
              (message: { key: string; params: string[] }) => {
                const keyCode: number = +message.key;
                this.logIfNan(keyCode, CBP_MESSAGE('ERROR_CODE'), message.key);
                const params: { entityType: string; entityIdentifier: string } = {
                  entityType: '',
                  entityIdentifier: '',
                };
                const cbpMessage: { [key: string]: string; translate?: string } = {};
                if (message.params.length) {
                  params.entityType = this.translate.instant(`INFO.${message.params[0]}.TITLE`);
                  params.entityIdentifier = message.params[1];
                }
                const res: string = this.translate.instant(CBP_MESSAGE(message.key));
                if (res.includes('CBP_MESSAGES')) {
                  console.log(res);
                }
                cbpMessage.translate = res;

                return { ...cbpMessage, params, key: CBP_MESSAGE(message.key), initialMessage: message };
              },
              () => {
                this.isShowLoader$.next(false);
              },
            )
            .filter((cbpMessage: any) => {
              return !cbpMessage.translate.includes('CBP_MESSAGES');
            })
            .filter((cbpMessage: any, index: number, allMessages: any) => {
              if (
                cbpMessage.key === CBP_MESSAGE(ENTITY_IDENTIFIER_NOT_ON_FILE) &&
                cbpMessage.initialMessage.params[0] === enumEntityModelCode.CONSIGNEE
              ) {
                const consigneeMessage: any = cbpMessage;
                const importerMessage: any = allMessages.find(
                  (item: any) =>
                    item.initialMessage && item.initialMessage.params && item.initialMessage.params[0] === enumEntityModelCode.IMPORTER,
                );
                if (!importerMessage) {
                  return true;
                }
                return (
                  importerMessage.initialMessage.params[1] !== consigneeMessage.initialMessage.params[1] &&
                  importerMessage.initialMessage.params[2] !== consigneeMessage.initialMessage.params[2]
                );
              }
              return true;
            });

          this.isShowLoader$.next(false);
          this.messages$.next(UtilsHelper.concat<any>(this.cbpMessages, this.errorMessages));
          this.updateDependency(id);
        });
    } else {
      this.messagesLoaded$.next();
    }
  }

  public fetchErrorMessages(id: number): void {
    const isErrorState: boolean = this.errorMessagesStates.includes(this.isf?.state);

    if (isErrorState) {
      this.isShowLoader$.next(true);
      this.isfMessagesRepository
        .getErrors(id, false, this.genericService.isPrinting$.getValue())
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: EntryErrorModel[]) => {
          this.errorMessages = UtilsHelper.uniqBy<any>(
            data
              .map(
                (message: EntryErrorModel) => {
                  const keyCode: number = +message.errorCode;
                  this.logIfNan(keyCode, 'CBP_MESSAGES.ERROR_CODE', message.errorCode);
                  const errorMessage: { [key: string]: string; translate?: string } = {};
                  const params: any = this.isfValidationService.getErrorParams(message, this.isf);

                  this.translate.get('ISF_ERROR_MESSAGES.' + message.errorCode, params).subscribe((res: string) => {
                    if (res.includes('ISF_ERROR_MESSAGES')) {
                      console.log(res);
                    }
                    errorMessage.translate = res;
                  });
                  return {
                    ...errorMessage,
                    key: 'ISF_ERROR_MESSAGES.' + message.errorCode,
                    params,
                    initialMessage: { key: message.errorCode, params },
                  };
                },
                () => {
                  this.isShowLoader$.next(false);
                },
              )
              .filter((message: any) => {
                return !message.translate.includes('ISF_ERROR_MESSAGES');
              }),
            'key',
          );

          this.isShowLoader$.next(false);
          this.messages$.next(UtilsHelper.concat<any>(this.cbpMessages, this.errorMessages));
          this.updateDependency(id);
        });
    }
  }

  private updateDependency(id: number): void {
    const depMessages: string[] = [CBP_MESSAGE(ENTITY_IDENTIFIER_NOT_ON_FILE)];
    const hasDepMessage: boolean = this.cbpMessages.some(
      (mess: { key: string }) =>
        depMessages.includes(mess.key) &&
        [enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE].includes(this.getEntityCodeFromCbp(mess)),
    );

    if (hasDepMessage) {
      this.dependenciesRepository
        .getDependenciesForISF(id)
        .pipe(take(1))
        .subscribe((dep: DependencyModel[]) => {
          this.importerDep = this.importer ? DependenciesService.findImporterDep(dep, this.importer) : null;
          this.consigneeDep = this.consignee ? DependenciesService.findConsigneeDep(dep, this.consignee) : null;

          this.isShowLoader$.next(false);
        });
    }
  }

  private getEntityCodeFromCbp(message: any): enumEntityModelCode {
    if (message.key === CBP_MESSAGE(ENTITY_IDENTIFIER_NOT_ON_FILE)) {
      return message.initialMessage.params[0] as enumEntityModelCode;
    }
    return null;
  }

  private getDepByKey(messageKey: string, entityCode: enumEntityModelCode): DependencyModel {
    return messageKey === CBP_MESSAGE(ENTITY_IDENTIFIER_NOT_ON_FILE) && entityCode === enumEntityModelCode.IMPORTER
      ? this.importerDep
      : messageKey === CBP_MESSAGE(ENTITY_IDENTIFIER_NOT_ON_FILE) && entityCode === enumEntityModelCode.CONSIGNEE
      ? this.consigneeDep
      : null;
  }

  private logIfNan(keyCode: number, message: string, code: string): void {
    if (!isNaN(keyCode)) {
      console.log(this.translate.instant(message, { code: code }));
    }
  }

  private get importer(): EntityModel {
    return this.isf?.importer;
  }

  private get consignee(): EntityModel {
    return IsfHelper.getIsfShipmentEntityByCode(this.isf, enumEntityModelCode.CONSIGNEE);
  }
}
